import { camelToSnakeCaseShallow } from '../queries/utils';
import { transportNullFields } from './transport-null';
import { transportEmptyArrayFields } from './transport-empty-arrays';

/**
 * Post-processes a proto `toJSON()` output to restore fields that proto's
 * serialization conventions strip but the backend API expects to receive.
 *
 * Proto `toJSON()` strips:
 * - Null scalar fields (guard: `!== undefined && !== null`)
 * - Empty array fields (guard: `?.length`)
 *
 * This utility re-adds both from the original user-state source object.
 *
 * Rationale: see ./transport-non-proto-fields.md
 * @see ./transport-non-proto-fields.md
 *
 * @param base - The snake_case object produced by proto `toJSON()` (fields stripped)
 * @param source - The original camelCase user-state object (fields present)
 * @returns A snake_case object with null fields and empty arrays restored
 *
 * @example
 * const protoOutput = { first_name: 'Ada' }; // toJSON() stripped phone_number:null and sources_of_wealth:[]
 * const userState = { firstName: 'Ada', phoneNumber: null, sourcesOfWealth: [] };
 * transportNonProtoFields(protoOutput, userState);
 * // => { first_name: 'Ada', phone_number: null, sources_of_wealth: [] }
 */
export function transportNonProtoFields(
  base: Record<string, unknown>,
  source: Record<string, unknown>
): Record<string, unknown> {
  const sourceSnake = camelToSnakeCaseShallow(source) as Record<string, unknown>;
  const withNulls = transportNullFields(base, sourceSnake);
  const withArrays = transportEmptyArrayFields(withNulls, sourceSnake);
  return withArrays;
}
